<template>
    <div class="uploadThirdParty_wrapper">
        <div class="uploadThirdParty_background-effect"></div>
        <div class="uploadThirdParty_container">
            <div class="uploadThirdParty_card">
                <div class="uploadThirdParty_header">
                    <div class="uploadThirdParty_status-fixed">
                        <div class="uploadThirdParty_status-container">
                            <div class="uploadThirdParty_status-indicator"></div>
                            <span class="uploadThirdParty_status-badge">Online</span>
                        </div>
                    </div>
                    
                    <div class="uploadThirdParty_header-container">
                        <div class="uploadThirdParty_logo">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/8/85/Logo-Test.png" alt="Logo da empresa" class="uploadThirdParty_logo-image" />
                        </div>
                        
                        <div class="uploadThirdParty_content-header">
                            <h1 class="uploadThirdParty_title">Upload de Documentos</h1>
                            <p class="uploadThirdParty_description">Envie seus documentos de forma segura e rápida.</p>
                        </div>
                    </div>
                </div>

                <div class="uploadThirdParty_content">
                    <!-- Formulário de Login (para páginas protegidas) -->
                    <div v-if="isProtectedPage && !isAuthenticated" class="uploadThirdParty_auth">
                        <div class="uploadThirdParty_auth-header">
                            <div class="uploadThirdParty_auth-icon">
                                <sw-icon name="shield" />
                            </div>
                            <div class="uploadThirdParty_auth-text">
                                <h3 class="uploadThirdParty_auth-title">Acesso Protegido</h3>
                                <p class="uploadThirdParty_auth-description">Faça login para acessar o upload de documentos</p>
                            </div>
                        </div>

                        <form @submit.prevent="handleAuthentication" class="uploadThirdParty_auth-form">
                            <div class="uploadThirdParty_auth-field">
                                <sw-input
                                    v-model="authCredentials.username"
                                    label="Usuário"
                                    icon="user"
                                    :validation="usernameValidation"
                                    @blur="validateUsername"
                                    placeholder="Digite seu usuário"
                                />
                            </div>

                            <div class="uploadThirdParty_auth-field">
                                <sw-input
                                    v-model="authCredentials.password"
                                    label="Senha"
                                    type="password"
                                    icon="lock"
                                    :validation="passwordValidation"
                                    @blur="validatePassword"
                                    placeholder="Digite sua senha"
                                />
                            </div>

                            <div class="uploadThirdParty_auth-error" v-if="authError">
                                <AlertMessage
                                    :type="ALERT_VARIANTS.ERROR"
                                    :description="authError"
                                />
                            </div>

                            <div class="uploadThirdParty_auth-actions">
                                <sw-button
                                    name="Acessar Upload"
                                    color="blue"
                                    icon="checkmark"
                                    type="submit"
                                    size="lg"
                                    :disabled="!isAuthFormValid || authenticating"
                                    :spin="authenticating"
                                />
                            </div>
                        </form>
                    </div>

                    <!-- Loading da Página -->
                    <div v-else-if="loadingPage" class="uploadThirdParty_loading">
                        <app-spinner :text="isLoadingAfterAuth ? 'Autenticando e carregando formulário...' : 'Carregando formulário...'" />
                    </div>

                    <!-- Conteúdo Principal (visível se não for protegido OU se estiver autenticado) -->
                    <template v-else-if="!isProtectedPage || isAuthenticated">
                        <!-- Indicador de Acesso Autenticado -->
                        <div v-if="isProtectedPage && isAuthenticated" class="uploadThirdParty_auth-status">
                            <div class="uploadThirdParty_auth-status-badge">
                                <sw-icon name="shield" class="uploadThirdParty_auth-status-icon" />
                                <span class="uploadThirdParty_auth-status-text">Acesso Autenticado</span>
                            </div>
                        </div>

                        <!-- Formulário de Upload -->
                        <form @submit.prevent="handleUpload">
                        <div class="uploadThirdParty_accordion-list">
                            <div class="uploadThirdParty_accordion-item" v-for="section in documentSections" :key="section.id">
                                <div 
                                    class="uploadThirdParty_accordion-header"
                                    :class="{ '--confirmed': section.confirmed }"
                                    @click="toggleAccordion(section)"
                                >
                                    <h3 class="uploadThirdParty_accordion-title">
                                        {{ section.title }}
                                        <sw-icon name="checkmark" class="uploadThirdParty_confirmed-icon" v-if="section.confirmed" />
                                    </h3>
                                    <sw-icon :name="section.open ? 'chevron-up' : 'chevron-down'" class="uploadThirdParty_accordion-toggle-icon" />
                                </div>
                                <div class="uploadThirdParty_accordion-content" v-if="section.open">
                                    <div class="uploadThirdParty_form-group" v-for="field in section.fields" :key="field.id">
                                        <template v-if="field.type === 'file'">
                                            <sw-input-file
                                                v-model="field.value"
                                                :label="field.label"
                                                label-button="Escolher Arquivo"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                                                :validation="field.validation"
                                                @update:modelValue="(file) => validateField(field, file)"
                                                :disabled="section.confirmed"
                                            />
                                        </template>
                                        <template v-else-if="field.type === 'text' && hasFileSelected(section)">
                                            <sw-input
                                                v-model="field.value"
                                                :label="field.label"
                                                :icon="field.icon"
                                                :validation="field.validation"
                                                @blur="validateField(field)"
                                                :disabled="section.confirmed"
                                                placeholder="Digite um nome personalizado para o arquivo"
                                            />
                                        </template>
                                        <template v-else-if="field.type === 'textarea'">
                                            <sw-textarea
                                                v-model="field.value"
                                                :label="field.label"
                                                :icon="field.icon"
                                                :validation="field.validation"
                                                @blur="validateField(field)"
                                                :disabled="section.confirmed"
                                            />
                                        </template>
                                    </div>
                                    <div class="uploadThirdParty_section-actions">
                                        <sw-button
                                            :name="section.confirmed ? 'Confirmado' : 'Confirmar'"
                                            :color="section.confirmed ? 'green' : 'blue'"
                                            :icon="section.confirmed ? 'checkmark' : 'check'"
                                            :disabled="!isSectionValid(section) || section.confirmed"
                                            @click="confirmSection(section)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="uploadThirdParty_actions">
                            <sw-button
                                name="Enviar Documentos"
                                color="blue"
                                icon="send"
                                type="submit"
                                :disabled="!isFormFullyConfirmed"
                            />
                        </div>
                        </form>
                    </template>
                </div>
                
                <div class="uploadThirdParty_footer">
                    <div class="uploadThirdParty_security">
                        <div class="uploadThirdParty_security-badges">
                            <div class="uploadThirdParty_security-badge">
                                <sw-icon name="shield" class="uploadThirdParty_security-icon" />
                                <span class="uploadThirdParty_security-text">Segurança por SSL</span>
                            </div>

                            <a href="https://filesave.com.br" target="_blank" rel="noopener noreferrer" class="uploadThirdParty_security-badge uploadThirdParty_security-link">
                                <sw-icon name="shield" class="uploadThirdParty_security-icon" />
                                <span class="uploadThirdParty_security-text">Protegido por FileSave</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { notify, loadingFull } from 'sysweb-vue-components/src/global';
import { authenticatePublicPageUser } from '@/services/mockApi';
import AlertMessage from '@Component/alerts/AlertMessage.vue';
import { ALERT_VARIANTS } from '@/constants';

const route = useRoute();

// Estados de autenticação
const isAuthenticated = ref(false);
const authenticating = ref(false);
const authError = ref('');
const isProtectedPage = ref(false);

// Estados de carregamento
const loadingPage = ref(true);
const isLoadingAfterAuth = ref(false);

// Credenciais do formulário
const authCredentials = ref({
    username: '',
    password: ''
});

// Validações dos campos
const usernameValidation = ref({ isValid: true, error: { message: '' } });
const passwordValidation = ref({ isValid: true, error: { message: '' } });

// Computed para verificar se o formulário de auth é válido
const isAuthFormValid = computed(() => {
    return authCredentials.value.username.length > 0 &&
           authCredentials.value.password.length > 0 &&
           usernameValidation.value.isValid &&
           passwordValidation.value.isValid;
});

const documentSections = ref([
    {
        id: 'notaFiscal',
        title: 'Nota Fiscal',
        open: true, // Começa aberto
        confirmed: false, // Novo estado
        fields: [
            { id: 'notaFiscalFile', label: 'Selecionar Documento', type: 'file', icon: 'file-text', required: true, value: null, validation: { isValid: true, error: { message: '' } } },
            { id: 'notaFiscalName', label: 'Nome do Arquivo (Opcional)', type: 'text', icon: 'edit', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
            { id: 'notaFiscalObs', label: 'Observação', type: 'textarea', icon: 'pencil', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
        ]
    },
    {
        id: 'impostoRenda',
        title: 'Imposto de Renda',
        open: false,
        confirmed: false,
        fields: [
            { id: 'impostoRendaFile', label: 'Selecionar Documento', type: 'file', icon: 'file-text', required: true, value: null, validation: { isValid: true, error: { message: '' } } },
            { id: 'impostoRendaName', label: 'Nome do Arquivo (Opcional)', type: 'text', icon: 'edit', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
            { id: 'impostoRendaObs', label: 'Observação', type: 'textarea', icon: 'pencil', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
        ]
    },
    {
        id: 'contratoSocial',
        title: 'Contrato Social',
        open: false,
        confirmed: false,
        fields: [
            { id: 'contratoSocialFile', label: 'Selecionar Documento', type: 'file', icon: 'file-text', required: true, value: null, validation: { isValid: true, error: { message: '' } } },
            { id: 'contratoSocialName', label: 'Nome do Arquivo (Opcional)', type: 'text', icon: 'edit', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
            { id: 'contratoSocialObs', label: 'Observação', type: 'textarea', icon: 'pencil', required: false, value: '', validation: { isValid: true, error: { message: '' } } },
        ]
    },
]);

// Função para alternar o estado do accordion
// Funções de validação para autenticação
function validateUsername() {
    const username = authCredentials.value.username;
    let isValid = true;
    let message = '';

    if (!username || username.length < 3) {
        isValid = false;
        message = 'Usuário deve ter pelo menos 3 caracteres.';
    }

    usernameValidation.value = { isValid, error: { message } };
}

function validatePassword() {
    const password = authCredentials.value.password;
    let isValid = true;
    let message = '';

    if (!password || password.length < 4) {
        isValid = false;
        message = 'Senha deve ter pelo menos 4 caracteres.';
    }

    passwordValidation.value = { isValid, error: { message } };
}

// Função de autenticação
async function handleAuthentication() {
    // Validar campos antes de enviar
    validateUsername();
    validatePassword();

    if (!isAuthFormValid.value) {
        authError.value = 'Por favor, preencha todos os campos corretamente.';
        return;
    }

    try {
        authenticating.value = true;
        authError.value = '';

        // Chamar API de autenticação
        const response = await authenticatePublicPageUser(authCredentials.value);

        if (response.status === 'success') {
            const userData = response.data;
            isAuthenticated.value = true;
            notify(`Acesso autorizado! Bem-vindo, ${userData.user.name}`, 'success');
            console.log('✅ Usuário autenticado:', userData);

            // Marcar que o próximo loading é pós-autenticação
            isLoadingAfterAuth.value = true;

            // Carregar dados da página após autenticação
            await loadPageData();
        } else {
            authError.value = response.message || 'Credenciais inválidas. Verifique seu usuário e senha.';
        }

    } catch (error) {
        console.error('❌ Erro na autenticação:', error);
        authError.value = 'Erro de conexão. Tente novamente.';
    } finally {
        authenticating.value = false;
    }
}

function toggleAccordion(section) {
    section.open = !section.open;
}

// Verifica se a seção tem um arquivo selecionado
function hasFileSelected(section) {
    return section.fields.some(field => field.type === 'file' && field.value);
}

// Valida se os campos de uma seção específica são válidos
const isSectionValid = (section) => {
    return section.fields.every(field => {
        if (!field.required && (field.value === '' || field.value === null)) {
            return true; // Campo não obrigatório e vazio é válido
        }
        return field.validation.isValid && (field.value !== '' && field.value !== null);
    });
};


// Valida se todas as seções foram confirmadas
const isFormFullyConfirmed = computed(() => {
    return documentSections.value.every(section => section.confirmed);
});

function validateField(field, value = field.value) {
    // Ao validar, desconfirmar a seção se ela estava confirmada
    const parentSection = documentSections.value.find(s => s.fields.some(f => f.id === field.id));
    if (parentSection && parentSection.confirmed) {
        parentSection.confirmed = false;
    }

    // Se for um campo de arquivo, atualizar o nome automaticamente
    if (field.type === 'file' && value) {
        const nameField = parentSection.fields.find(f => f.type === 'text');
        if (nameField && !nameField.value) {
            // Pegar nome do arquivo sem a extensão
            const fileName = value.name;
            const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
            nameField.value = nameWithoutExtension;
        }
    }

    let isValid = true;
    let message = '';

    if (field.required && (value === '' || value === null)) {
        isValid = false;
        message = `${field.label} é obrigatório.`;
    } else if (field.type === 'textarea' && value.length > 500) {
        isValid = false;
        message = 'Observação muito longa (máx. 500 caracteres).';
    }

    field.validation = { isValid, error: { message } };
}

function confirmSection(section) {
    // Força a validação dos campos da seção antes de confirmar
    section.fields.forEach(field => validateField(field));

    if (isSectionValid(section)) {
        section.confirmed = true;
        section.open = false; // Minimiza o accordion após confirmação
        notify(`Seção '${section.title}' confirmada com sucesso!`, 'success');
    } else {
        notify(`Por favor, preencha todos os campos obrigatórios da seção '${section.title}' corretamente.`, 'warning');
    }
}

async function handleUpload() {
    // Verifica se todas as seções foram confirmadas
    if (!isFormFullyConfirmed.value) {
        notify('Por favor, confirme todas as seções antes de enviar os documentos.', 'warning');
        return;
    }

    loadingFull({
        enabled: true,
        text: 'Enviando documentos...',
        subText: 'Isso pode levar alguns instantes, dependendo do tamanho dos arquivos.'
    });

    try {
        const formData = new FormData();
        documentSections.value.forEach(section => {
            section.fields.forEach(field => {
                if (field.type === 'file' && field.value) {
                    formData.append(field.id, field.value);
                } else if (field.value !== null && field.value !== '') {
                    formData.append(field.id, field.value);
                }
            });
        });

        // Simular upload de arquivo - agora sempre com sucesso se tudo confirmado
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Aqui você enviaria o formData para sua API
        // Ex: await axios.post('/api/upload', formData);

        notify('Documentos enviados com sucesso! Todos os arquivos foram processados.', 'success');
        
        // Resetar formulário após sucesso
        documentSections.value.forEach(section => {
            section.fields.forEach(field => {
                field.value = (field.type === 'file') ? null : '';
                field.validation = { isValid: true, error: { message: '' } };
            });
            section.confirmed = false; // Resetar estado de confirmação
            section.open = false; // Fechar accordion após reset
        });

        // Reabrir a primeira seção após reset para facilitar novo uso
        if (documentSections.value.length > 0) {
            documentSections.value[0].open = true;
        }

    } catch (error) {
        notify('Erro ao enviar documentos. Por favor, tente novamente.', 'error');
    } finally {
        loadingFull({ enabled: false });
    }
}

// Função para simular carregamento da página
async function loadPageData() {
    try {
        loadingPage.value = true;

        // Simular carregamento dos dados da página (configurações, seções, etc.)
        await new Promise(resolve => setTimeout(resolve, Math.floor(Math.random() * 1500) + 800));

        console.log('📄 Dados da página carregados');

    } catch (error) {
        console.error('❌ Erro ao carregar dados da página:', error);
        notify('Erro ao carregar a página. Tente recarregar.', 'error');
    } finally {
        loadingPage.value = false;
        isLoadingAfterAuth.value = false;
    }
}

onMounted(async () => {
    // Verificar se a página requer autenticação baseado no parâmetro 'protected'
    isProtectedPage.value = route.query.protected === 'true';

    // Simular carregamento da página (apenas se não for protegida OU se já estiver autenticado)
    if (!isProtectedPage.value) {
        await loadPageData();
    } else {
        // Para páginas protegidas, não carregar dados até autenticar
        loadingPage.value = false;
    }
});
</script>

<style scoped lang="scss">
.uploadThirdParty_wrapper {
    min-height: 100vh;
    background-color: var(--greyLight-1, #f8f9fa);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5em;
    font-family: 'Inter', sans-serif;

    @media (max-width: 768px) {
        padding: 1em;
    }
}

.uploadThirdParty_container {
    width: 100%;
    max-width: 50em;
}

.uploadThirdParty_card {
    background-color: var(--white, #ffffff);
    border-radius: 1em;
    box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.08);
    border: 1px solid var(--greyLight-2, #e5e7eb);
    overflow: hidden;
}

// Header Section
.uploadThirdParty_header {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    padding: 2em 2em;
    border-radius: 1em 1em 0 0;
    color: var(--white, #ffffff);
    position: relative;
    overflow: hidden;
    text-align: center;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        z-index: 0;
    }

    @media (max-width: 768px) {
        padding: 1.5em 1.5em;
    }
}

.uploadThirdParty_status-fixed {
    position: absolute;
    top: 1.5em;
    right: 1.5em;
    z-index: 2;

    @media (max-width: 768px) {
        top: 1em;
        right: 1em;
    }
}

.uploadThirdParty_header-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5em;
    position: relative;
    z-index: 1;
    max-width: 40em;
    margin: 0 auto;
    padding-top: 1em;

    @media (max-width: 768px) {
        gap: 1.25em;
        padding-top: 0.5em;
    }
}

.uploadThirdParty_logo {
    display: flex;
    justify-content: center;
}

.uploadThirdParty_logo-image {
    width: 5em;
    height: 5em;
    border-radius: 1em;
    object-fit: contain;
    object-position: center;
    border: 2px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0.5em 1.5em rgba(0, 0, 0, 0.2);
    background-color: rgba(255, 255, 255, 0.1);
    transition: transform 0.3s ease;
    display: block;

    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        width: 4em;
        height: 4em;
    }
}

.uploadThirdParty_content-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
    text-align: center;
}

.uploadThirdParty_title {
    font-size: 1.75em;
    font-weight: 700;
    color: var(--white, #ffffff);
    margin: 0;
    text-shadow: 0 0.125em 0.5em rgba(0, 0, 0, 0.3);
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 1.5em;
    }
}

.uploadThirdParty_description {
    font-size: 0.95em;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
    text-shadow: 0 0.125em 0.25em rgba(0, 0, 0, 0.2);
    max-width: 28em;

    @media (max-width: 768px) {
        font-size: 0.9em;
    }
}

.uploadThirdParty_status-container {
    display: flex;
    align-items: center;
    gap: 0.5em;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    padding: 0.625em 1em;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0.25em 0.75em rgba(0, 0, 0, 0.1);
}

.uploadThirdParty_status-indicator {
    width: 0.625em;
    height: 0.625em;
    background-color: #10b981;
    border-radius: 50%;
    box-shadow: 0 0 0 0.125em rgba(16, 185, 129, 0.4);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    }
    70% {
        box-shadow: 0 0 0 0.625em rgba(16, 185, 129, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
}

.uploadThirdParty_status-badge {
    font-size: 0.8em;
    font-weight: 600;
    color: var(--white, #ffffff);
    text-shadow: 0 0.125em 0.25em rgba(0, 0, 0, 0.2);
    letter-spacing: 0.025em;
}

// Content Section
.uploadThirdParty_content {
    padding: 2em;

    @media (max-width: 768px) {
        padding: 1.5em;
    }
}

// Footer Section
.uploadThirdParty_footer {
    background: var(--greyLight-3, #f3f4f6);
    padding: 2em;
    border-radius: 0 0 1em 1em;
    border-top: 1px solid var(--greyLight-2, #e5e7eb);
    text-align: center;

    @media (max-width: 768px) {
        padding: 1.5em;
    }
}

.uploadThirdParty_security {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 40em;
    margin: 0 auto;
}

.uploadThirdParty_security-badges {
    display: flex;
    gap: 2em;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
        gap: 1.5em;
    }

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 1.25em;
        align-items: center;
    }
}

.uploadThirdParty_security-badge {
    display: flex;
    align-items: center;
    gap: 0.5em;
    background: var(--white, #ffffff);
    padding: 0.75em 1em;
    border-radius: 0.75em;
    border: 1px solid var(--greyLight-2, #e5e7eb);
    box-shadow: 0 0.125em 0.5em rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    flex: 1;
    justify-content: center;
    min-width: 0;

    &:hover {
        box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.12);
        transform: translateY(-0.125em);
    }

    @media (max-width: 768px) {
        flex: none;
        min-width: auto;
    }
}

.uploadThirdParty_security-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.02);
    }
}

.uploadThirdParty_security-icon {
    font-size: 1em;
    color: #10b981;
    width: 1em;
    height: 1em;
    display: inline-block;
    flex-shrink: 0;
    transition: color 0.2s ease;

    .uploadThirdParty_security-link:hover & {
        color: #059669;
    }
}

.uploadThirdParty_security-text {
    font-size: 0.875em;
    font-weight: 600;
    color: var(--greyDark-1, #1f2937);
    white-space: nowrap;
}

.uploadThirdParty_accordion-list {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
    margin-bottom: 2em;
}

.uploadThirdParty_accordion-item {
    background: var(--white, #ffffff);
    border-radius: 1em;
    border: 1px solid var(--greyLight-2, #e5e7eb);
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.04);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 0.25em;
        height: 100%;
        background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
        transition: all 0.3s ease;
    }

    &:hover {
        box-shadow: 0 0.5em 2em rgba(0, 0, 0, 0.08);
        transform: translateY(-0.125em);
        border-color: #c7d2fe;

        &::before {
            background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
            width: 0.375em;
        }
    }

    &.--confirmed {
        border-color: #10b981;
        box-shadow: 0 0.5em 1.5em rgba(16, 185, 129, 0.15);

        &::before {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            width: 0.375em;
        }
    }
}

.uploadThirdParty_accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5em 2em;
    cursor: pointer;
    background: linear-gradient(135deg, #fafbff 0%, #f8fafc 100%);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.1), transparent);
        transition: left 0.6s ease;
    }

    &:hover {
        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);

        &::after {
            left: 100%;
        }

        .uploadThirdParty_accordion-toggle-icon {
            transform: scale(1.1);
            color: #6366f1;
        }
    }

    &.--confirmed {
        background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
        
        &::after {
            background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent);
        }

        .uploadThirdParty_accordion-title {
            color: #065f46;
        }

        .uploadThirdParty_accordion-toggle-icon {
            color: #10b981;
        }

        &:hover {
            background: linear-gradient(135deg, #d1fae5 0%, #bbf7d0 100%);
        }
    }

    @media (max-width: 768px) {
        padding: 1.25em 1.5em;
    }
}

.uploadThirdParty_accordion-title {
    font-size: 1.25em;
    font-weight: 700;
    color: var(--greyDark-1, #1e293b);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75em;
    letter-spacing: -0.025em;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
        font-size: 1.1em;
    }
}

.uploadThirdParty_confirmed-icon {
    font-size: 1.25em;
    color: #10b981;
    filter: drop-shadow(0 0.125em 0.25em rgba(16, 185, 129, 0.3));
    animation: confirmBounce 0.6s ease-out;
}

@keyframes confirmBounce {
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
}

.uploadThirdParty_accordion-toggle-icon {
    font-size: 1.25em;
    color: var(--greyDark-2, #64748b);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 0.125em 0.25em rgba(0, 0, 0, 0.1));

    .uploadThirdParty_accordion-item:hover & {
        transform: scale(1.1);
    }
}

.uploadThirdParty_accordion-content {
    padding: 2em;
    background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
    border-top: 1px solid var(--greyLight-2, #e2e8f0);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 2em;
        right: 2em;
        height: 1px;
        background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
    }

    @media (max-width: 768px) {
        padding: 1.5em;

        &::before {
            left: 1.5em;
            right: 1.5em;
        }
    }
}

.uploadThirdParty_form-group {
    margin-bottom: 1.5em;
    text-align: left;

    &:last-child {
        margin-bottom: 0;
    }
}

.uploadThirdParty_section-actions {
    margin-top: 1.5em;
    display: flex;
    justify-content: flex-end; /* Alinhar o botão à direita */

    .sw-button {
        max-width: 10em; /* Botão menor para a seção */
        padding: 0.8em 1.5em;
        font-size: 0.9em;
        border-radius: 0.5em;
        font-weight: 600;
    }
}

.uploadThirdParty_actions {
    margin-top: 3.5em;
    display: flex;
    justify-content: center;

    .sw-button {
        width: 100%;
        max-width: 20em;
        padding: 1.2em 2.5em;
        font-size: 1.1em;
        border-radius: 0.8em;
        font-weight: 600;
        box-shadow: 0 0.5em 1.5em rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-0.2em);
            box-shadow: 0 0.8em 2em rgba(0, 0, 0, 0.15);
        }
    }
}

// Loading da Página
.uploadThirdParty_loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4em 2em;
    text-align: center;
}

// Formulário de Autenticação Integrado
.uploadThirdParty_auth {
    text-align: center;
    max-width: 28em;
    margin: 0 auto;
    padding: 2em 0;
}

.uploadThirdParty_auth-header {
    display: flex;
    align-items: center;
    gap: 1em;
    justify-content: center;
    margin-bottom: 2em;
}

.uploadThirdParty_auth-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.75em;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    font-size: 1.25em;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    flex-shrink: 0;
}

.uploadThirdParty_auth-text {
    text-align: left;
}

.uploadThirdParty_auth-title {
    font-size: 1.5em;
    font-weight: 700;
    color: var(--greyDark-1, #1f2937);
    margin: 0 0 0.25em 0;
    line-height: 1.2;
}

.uploadThirdParty_auth-description {
    font-size: 0.9em;
    color: var(--greyDark-2, #6b7280);
    margin: 0;
    line-height: 1.3;
}

.uploadThirdParty_auth-form {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
    text-align: left;
}

.uploadThirdParty_auth-field {
    position: relative;
}

.uploadThirdParty_auth-error {
    margin: -0.5em 0 1em 0;
}

.uploadThirdParty_auth-actions {
    margin-top: 0.5em;

    .sw-button {
        width: 100%;
    }
}

// Badge de Status Autenticado
.uploadThirdParty_auth-status {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5em;
}

.uploadThirdParty_auth-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05));
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 9999px;
    padding: 0.75em 1.25em;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    &:hover {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08));
        border-color: rgba(16, 185, 129, 0.3);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
    }
}

.uploadThirdParty_auth-status-icon {
    font-size: 1em;
    color: #10b981;
}

.uploadThirdParty_auth-status-text {
    font-size: 0.875em;
    font-weight: 600;
    color: #047857;
    letter-spacing: 0.025em;
}

// Responsividade para autenticação
@media (max-width: 768px) {
    .uploadThirdParty_auth {
        padding: 1.5em 0;
    }

    .uploadThirdParty_auth-header {
        flex-direction: column;
        gap: 0.75em;
        margin-bottom: 1.5em;
    }

    .uploadThirdParty_auth-icon {
        width: 2.25em;
        height: 2.25em;
        font-size: 1.1em;
    }

    .uploadThirdParty_auth-text {
        text-align: center;
    }

    .uploadThirdParty_auth-title {
        font-size: 1.375em;
    }

    .uploadThirdParty_auth-form {
        gap: 1.25em;
    }

    .uploadThirdParty_auth-status {
        margin-bottom: 1.25em;
    }

    .uploadThirdParty_auth-status-badge {
        padding: 0.625em 1em;
        gap: 0.375em;
    }

    .uploadThirdParty_auth-status-icon {
        font-size: 0.875em;
    }

    .uploadThirdParty_auth-status-text {
        font-size: 0.8125em;
    }
}
</style>